import { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'

const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

export default function ManageTarget() {
  const [targets, setTargets] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [showMonthlyCap, setShowMonthlyCap] = useState(false);
  const [showDailyCap, setShowDailyCap] = useState(false);
  const [showWeeklyCap, setShowWeeklyCap] = useState(false);
  const [showMaxConcurrency, setShowMaxConcurrency] = useState(false);

  useEffect(() => {
    fetchTargets();
    fetchBuyers();
  }, []);

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/campaigns/targets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      
      // Check for empty or null values
      const validTargets = data.data.filter(target => {
        if (!target.id || !target.campaign_name) {
          return false;
        }
        return true;
      });

      if (data.success) {
        setTargets(validTargets);
      }
    } catch (error) {
      console.error('Error fetching targets:', error);
    }
  };

  const fetchBuyers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/buyers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setBuyers(data.data);
      }
    } catch (error) {
      console.error('Error fetching buyers:', error);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const target = targets.find(t => t.id === id);
      
      if (!target) return;

      const response = await fetch(`${API_URL}/campaigns/${target.campaign_id}/targets/${id}/toggle-status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTargets(prevTargets => {
          const newTargets = prevTargets.map(target => 
            target.id === id 
              ? { ...target, status: !target.status }
              : target
          );
          return newTargets;
        });
      } else {
        console.error('Failed to toggle status:', data.error);
        alert(data.error || 'Failed to toggle status');
      }
    } catch (error) {
      console.error('Error toggling target status:', error);
      alert('Error toggling target status');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this target?')) {
      try {
        const token = localStorage.getItem('token');
        // Get the campaign ID from the target
        const target = targets.find(t => t.id === id);
        if (!target) return;

        const response = await fetch(`${API_URL}/campaigns/${target.campaign_id}/targets/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        if (data.success) {
          // Update the local state by removing the deleted target
          setTargets(prevTargets => prevTargets.filter(target => target.id !== id));
        } else {
          console.error('Failed to delete target:', data.error);
          alert(data.error || 'Failed to delete target');
        }
      } catch (error) {
        console.error('Error deleting target:', error);
        alert('Error deleting target');
      }
    }
  };

  // Update handleEdit to include flag states
  const handleEdit = (target) => {
    setEditingId(target.id);
    setEditForm({
      campaign_id: target.campaign_id,
      buyer_id: target.buyer_id,
      target_name: target.target_name,
      target_number: target.target_number,
      max_concurrency: target.max_concurrency || 0,
      monthly_cap: target.monthly_cap || 0,
      daily_cap: target.daily_cap || 0,
      weekly_cap: target.weekly_cap || 0,
      connection_timeout: target.connection_timeout || 0,
      live: target.live
    });
    // Update to handle case-insensitive comparison
    setShowMonthlyCap(target.monthly_cap_flag?.toLowerCase() === "yes");
    setShowDailyCap(target.daily_cap_flag?.toLowerCase() === "yes");
    setShowWeeklyCap(target.weekly_cap_flag?.toLowerCase() === "yes");
    setShowMaxConcurrency(target.cc_limit_flag?.toLowerCase() === "yes");
    setIsEditModalOpen(true);
  };

  // Update handleEditChange to handle numeric fields
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    
    // Define numeric fields
    const numericFields = [
      'target_number',
      'max_concurrency',
      'monthly_cap',
      'daily_cap',
      'weekly_cap',
      'connection_timeout'
    ];

    if (numericFields.includes(name)) {
      // Remove non-numeric characters and trim whitespace
      const numericValue = value.replace(/[^\d]/g, '').trim();
      setEditForm(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      // For non-numeric fields, just trim whitespace
      setEditForm(prev => ({
        ...prev,
        [name]: value.trim()
      }));
    }
  };

  // Update handleSaveEdit to include flags
  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/campaigns/${editForm.campaign_id}/targets/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          buyer_id: editForm.buyer_id,
          target_name: editForm.target_name,
          target_number: editForm.target_number,
          max_concurrency: editForm.max_concurrency,
          monthly_cap: editForm.monthly_cap,
          daily_cap: editForm.daily_cap,
          weekly_cap: editForm.weekly_cap,
          connection_timeout: editForm.connection_timeout,
          live: editForm.live,
          cc_limit_flag: showMaxConcurrency ? "Yes" : "No",
          monthly_cap_flag: showMonthlyCap ? "Yes" : "No",
          daily_cap_flag: showDailyCap ? "Yes" : "No",
          weekly_cap_flag: showWeeklyCap ? "Yes" : "No"
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchTargets();
        setIsEditModalOpen(false);
        setEditingId(null);
        setEditForm({});
      } else {
        console.error('Failed to update target:', data.error);
        alert(data.error || 'Failed to update target');
      }
    } catch (error) {
      console.error('Error updating target:', error);
      alert('Error updating target');
    }
  };

  // Modify handleCancelEdit to close modal
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingId(null);
    setEditForm({});
  };

  // Add this function to filter targets
  const filteredTargets = targets.filter((target) =>
    Object.values(target).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Add this new function to handle individual field updates
  const handleFieldUpdate = async (targetId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const target = targets.find(t => t.id === targetId);
      if (!target) return;

      const response = await fetch(`${API_URL}/campaigns/${target.campaign_id}/targets/${targetId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          buyer_id: target.buyer_id,
          target_name: target.target_name,
          target_number: target.target_number,
          max_concurrency: target.max_concurrency,
          monthly_cap: target.monthly_cap,
          daily_cap: target.daily_cap,
          weekly_cap: target.weekly_cap,
          connection_timeout: target.connection_timeout,
          live: target.live,
          [field]: value // Override the specific field being edited
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setTargets(prevTargets =>
          prevTargets.map(t =>
            t.id === targetId
              ? { ...t, [field]: value }
              : t
          )
        );
        setEditingField(null);
        setEditingId(null);
        setNewValue("");
      } else {
        console.error('Failed to update field:', data.error);
        alert(data.error || 'Failed to update field');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Error updating field');
    }
  };

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Manage Targets
                </h1>
              </div>

              {/* Add search input */}
              <div className="mt-4 sm:mt-0 sm:ml-16">
                <input
                  type="text"
                  placeholder="Search targets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
              </div>
            </div>

            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0"
                        >
                          Buyer
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Campaign Name
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Target Name
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Number
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Weight
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          CC
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Today
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Weekly
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Monthly
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filteredTargets.map((target) => {
                        if (!target.id) {
                          return null;
                        }
                        return (
                          <tr key={target.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                              {target.buyer_name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.campaign_name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.target_name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.type}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.target_number}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.weight}%
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {target.max_concurrency}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {editingField === "daily_cap" &&
                              editingId === target.id ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={newValue}
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                    className="bg-gray-700 text-white p-1 rounded w-20"
                                    min="0"
                                  />
                                  <button
                                    onClick={() =>
                                      handleFieldUpdate(
                                        target.id,
                                        "daily_cap",
                                        newValue
                                      )
                                    }
                                    className="text-green-500 hover:text-green-400"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingField(null);
                                      setEditingId(null);
                                      setNewValue("");
                                    }}
                                    className="text-red-500 hover:text-red-400"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="cursor-pointer hover:text-blue-400"
                                  onClick={() => {
                                    setEditingField("daily_cap");
                                    setEditingId(target.id);
                                    setNewValue(
                                      target.daily_cap?.toString() || "0"
                                    );
                                  }}
                                >
                                  {target.daily_cap}
                                </div>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {editingField === "weekly_cap" &&
                              editingId === target.id ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={newValue}
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                    className="bg-gray-700 text-white p-1 rounded w-20"
                                    min="0"
                                  />
                                  <button
                                    onClick={() =>
                                      handleFieldUpdate(
                                        target.id,
                                        "weekly_cap",
                                        newValue
                                      )
                                    }
                                    className="text-green-500 hover:text-green-400"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingField(null);
                                      setEditingId(null);
                                      setNewValue("");
                                    }}
                                    className="text-red-500 hover:text-red-400"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="cursor-pointer hover:text-blue-400"
                                  onClick={() => {
                                    setEditingField("weekly_cap");
                                    setEditingId(target.id);
                                    setNewValue(
                                      target.weekly_cap?.toString() || "0"
                                    );
                                  }}
                                >
                                  {target.weekly_cap}
                                </div>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {editingField === "monthly_cap" &&
                              editingId === target.id ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={newValue}
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                    className="bg-gray-700 text-white p-1 rounded w-20"
                                    min="0"
                                  />
                                  <button
                                    onClick={() =>
                                      handleFieldUpdate(
                                        target.id,
                                        "monthly_cap",
                                        newValue
                                      )
                                    }
                                    className="text-green-500 hover:text-green-400"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingField(null);
                                      setEditingId(null);
                                      setNewValue("");
                                    }}
                                    className="text-red-500 hover:text-red-400"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="cursor-pointer hover:text-blue-400"
                                  onClick={() => {
                                    setEditingField("monthly_cap");
                                    setEditingId(target.id);
                                    setNewValue(
                                      target.monthly_cap?.toString() || "0"
                                    );
                                  }}
                                >
                                  {target.monthly_cap}
                                </div>
                              )}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-xs font-medium sm:pr-0">
                              <div className="flex items-center space-x-3">
                                <Switch
                                  checked={target.status}
                                  onChange={() => handleStatusToggle(target.id)}
                                  className={`${
                                    target.status
                                      ? "bg-green-600"
                                      : "bg-gray-600"
                                  } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                                >
                                  <span className="sr-only">Toggle status</span>
                                  <span
                                    className={`${
                                      target.status
                                        ? "translate-x-4"
                                        : "translate-x-1"
                                    } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                  />
                                </Switch>
                                <button
                                  onClick={() => handleEdit(target)}
                                  className="text-indigo-400 hover:text-indigo-300"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(target.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Transition.Root show={isEditModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative justify-center z-50 overflow-y-auto"
          onClose={setIsEditModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900 bg-opacity-80 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div>
                    <div className="mt-1 text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-black mb-4"
                      >
                        Edit Target
                      </Dialog.Title>

                      <div className="mt-2 space-y-2">
                        {/* Buyer ID */}
                        <div className="text-left">
                          <label
                            htmlFor="buyer_id"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Buyer *
                          </label>
                          <select
                            name="buyer_id"
                            id="buyer_id"
                            value={editForm.buyer_id || ""}
                            onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="" className="text-xs">
                              Select a buyer
                            </option>
                            {buyers.map((buyer) => (
                              <option key={buyer.id} value={buyer.id}>
                                {buyer.name} (ID: {buyer.id})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Target Name */}
                        <div className="text-left">
                          <label
                            htmlFor="target_name"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Target Name *
                          </label>
                          <input
                            type="text"
                            name="target_name"
                            id="target_name"
                            value={editForm.target_name || ""}
                            onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>

                        {/* Target Number */}
                        <div className="text-left">
                          <label
                            htmlFor="target_number"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Target Number *
                          </label>
                          <input
                            type="number"
                            name="target_number"
                            id="target_number"
                            value={editForm.target_number || ""}
                            onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                            min="1"
                          />
                        </div>

                        {/* Max Concurrency with Checkbox */}
                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label
                              htmlFor="max_concurrency"
                              className="block text-sm font-medium text-gray-900"
                            >
                              Max Concurrency
                            </label>
                            <input
                              type="checkbox"
                              checked={showMaxConcurrency}
                              onChange={() =>
                                setShowMaxConcurrency(!showMaxConcurrency)
                              }
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showMaxConcurrency && (
                            <input
                              type="number"
                              name="max_concurrency"
                              id="max_concurrency"
                              value={editForm.max_concurrency || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="1"
                            />
                          )}
                        </div>

                        {/* Caps and Timeout */}

                        {/* Monthly Cap with Checkbox */}
                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label
                              htmlFor="monthly_cap"
                              className="block text-sm font-medium text-gray-900"
                            >
                              Monthly Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showMonthlyCap}
                              onChange={() =>
                                setShowMonthlyCap(!showMonthlyCap)
                              }
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showMonthlyCap && (
                            <input
                              type="number"
                              name="monthly_cap"
                              id="monthly_cap"
                              value={editForm.monthly_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>

                        {/* Daily Cap with Checkbox */}
                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label
                              htmlFor="daily_cap"
                              className="block text-sm font-medium text-gray-900"
                            >
                              Daily Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showDailyCap}
                              onChange={() => setShowDailyCap(!showDailyCap)}
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showDailyCap && (
                            <input
                              type="number"
                              name="daily_cap"
                              id="daily_cap"
                              value={editForm.daily_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>

                        {/* Weekly Cap with Checkbox */}
                        <div className="text-left">
                          <div className="flex items-center justify-between">
                            <label
                              htmlFor="weekly_cap"
                              className="block text-sm font-medium text-gray-900"
                            >
                              Weekly Cap
                            </label>
                            <input
                              type="checkbox"
                              checked={showWeeklyCap}
                              onChange={() => setShowWeeklyCap(!showWeeklyCap)}
                              className="form-checkbox h-4 w-4 text-indigo-600"
                            />
                          </div>
                          {showWeeklyCap && (
                            <input
                              type="number"
                              name="weekly_cap"
                              id="weekly_cap"
                              value={editForm.weekly_cap || ""}
                              onChange={handleEditChange}
                              className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              min="0"
                            />
                          )}
                        </div>

                        {/* Connection Timeout */}
                        <div className="text-left">
                          <label
                            htmlFor="connection_timeout"
                            className="block text-sm font-medium text-gray-900"
                          >
                            Connection Timeout
                          </label>
                          <input
                            type="number"
                            name="connection_timeout"
                            id="connection_timeout"
                            value={editForm.connection_timeout || ""}
                            onChange={handleEditChange}
                            className="p-1 block w-full rounded-md bg-gray-200 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSaveEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
